import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Mail, Ban, Trash2, Check, User as UserIcon, ShieldCheck, Save, Send, Play } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fileUrl } from "../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [detail, setDetail] = useState(null); // {user, profile, videos, stats}
  const [detailOpen, setDetailOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [verifyToggling, setVerifyToggling] = useState(false);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [msgDraft, setMsgDraft] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const openDetail = async (userId) => {
    try {
      const { data } = await api.get(`/admin/users/${userId}/full`);
      setDetail(data);
      setNotesDraft(data?.profile?.admin_notes || "");
      setDetailOpen(true);
      // Load thread with this user (admin ↔ user)
      if (data?.user?.role === "player") {
        try {
          const { data: msgs } = await api.get(`/messages/${userId}`);
          setThreadMsgs(msgs || []);
        } catch { setThreadMsgs([]); }
      } else {
        setThreadMsgs([]);
      }
      setMsgDraft("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleVerify = async () => {
    if (!detail?.user) return;
    setVerifyToggling(true);
    try {
      const next = !detail.profile?.verified;
      await api.patch(`/admin/players/${detail.user.id}/verify`, { verified: next });
      setDetail((d) => ({ ...d, profile: { ...(d.profile || {}), verified: next } }));
      // Refresh stats
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (err) { console.debug("stats refresh failed", err?.message); }
      toast.success(next ? "Player verified · badge activated" : "Verification removed");
    } catch (e) { toast.error(formatApiError(e)); } finally { setVerifyToggling(false); }
  };

  const saveNotes = async () => {
    if (!detail?.user) return;
    setSavingNotes(true);
    try {
      await api.patch(`/admin/players/${detail.user.id}/notes`, { admin_notes: notesDraft });
      setDetail((d) => ({ ...d, profile: { ...(d.profile || {}), admin_notes: notesDraft } }));
      toast.success("Notes saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingNotes(false); }
  };

  const sendMessageToPlayer = async () => {
    if (!detail?.user || !msgDraft.trim()) return;
    setSendingMsg(true);
    try {
      const { data } = await api.post("/messages", { to_user_id: detail.user.id, text: msgDraft.trim() });
      setThreadMsgs((m) => [...m, data]);
      setMsgDraft("");
      toast.success("Message sent to player");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSendingMsg(false); }
  };

  const loadInquiries = async () => {
    try { const { data } = await api.get("/admin/inquiries"); setInquiries(data); } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(s.data); setUsers(u.data);
        loadInquiries();
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}?status=${status}`);
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBan = async (u) => {
    try {
      const nextBanned = !u.is_banned;
      await api.patch(`/admin/users/${u.id}/ban?banned=${nextBanned}`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_banned: nextBanned } : x)));
      toast.success(nextBanned ? "User banned" : "User unbanned");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`Deleted ${u.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cards = [
    ["Players", stats.players, "#e65f2b"],
    ["Verified", stats.verified, "#eab308"],
    ["Scouts", stats.scouts, "#2A9D8F"],
    ["Coaches", stats.coaches, "#e65f2b"],
    ["Videos", stats.videos, "#eab308"],
    ["Invites", stats.invitations, "#2A9D8F"],
  ];

  const newCount = inquiries.filter((i) => i.status === "new").length;

  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/trend-digest/send-now");
      toast.success(`Digest sent — ${data.sent} sent, ${data.skipped} skipped, ${data.no_data} without data`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSendingDigest(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">Admin dashboard</div>
            <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight">Platform overview</h1>
          </div>
          <Button
            onClick={sendDigestNow}
            disabled={sendingDigest}
            data-testid="admin-send-digest-btn"
            className="bg-[#eab308] hover:bg-[#d69e08] text-black font-bold uppercase tracking-widest rounded-sm h-10"
          >
            {sendingDigest ? "Sending…" : "Send trend digest"}
          </Button>
        </div>

        <div className="mt-6 grid md:grid-cols-6 gap-4">
          {cards.map(([label, val, color]) => (
            <div key={label} className="border border-white/10 bg-[#141414] p-6" data-testid={`stat-${label.toLowerCase()}`}>
              <div className="text-xs uppercase tracking-widest text-white/60">{label}</div>
              <div className="mt-2 font-display text-4xl font-bold" style={{ color }}>{val ?? 0}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="users" className="mt-8">
          <TabsList className="bg-transparent border-b border-white/10 rounded-none w-full justify-start p-0 h-auto">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#e65f2b] data-[state=active]:bg-transparent data-[state=active]:text-[#e65f2b] uppercase tracking-widest text-xs font-bold px-4 py-3">Users</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#e65f2b] data-[state=active]:bg-transparent data-[state=active]:text-[#e65f2b] uppercase tracking-widest text-xs font-bold px-4 py-3">
              Inquiries {newCount > 0 && <span className="ml-2 bg-[#e65f2b] text-black px-2 rounded-full text-[10px]">{newCount}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <div className="border border-white/10 bg-[#141414] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-white/60">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Created</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5" data-testid={`admin-user-${u.id}`}>
                      <td className="p-3">{u.name}</td>
                      <td className="p-3">
                        <button
                          onClick={() => openDetail(u.id)}
                          data-testid={`user-email-link-${u.id}`}
                          className="text-white hover:text-[#e65f2b] underline underline-offset-4 decoration-white/20 hover:decoration-[#e65f2b] transition-colors"
                        >{u.email}</button>
                      </td>
                      <td className="p-3"><span className="text-xs uppercase tracking-widest text-[#eab308]">{u.role}</span></td>
                      <td className="p-3">
                        {u.is_banned ? (
                          <span className="text-xs uppercase tracking-widest text-red-400 border border-red-400/40 px-2 py-1">Banned</span>
                        ) : (
                          <span className="text-xs uppercase tracking-widest text-[#2A9D8F]">Active</span>
                        )}
                      </td>
                      <td className="p-3 text-white/50">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="p-3 text-right">
                        {u.role === "admin" ? (
                          <span className="text-xs text-white/30">Protected</span>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => toggleBan(u)}
                              data-testid={`user-ban-${u.id}`}
                              className={`px-2 py-1 text-xs uppercase tracking-widest rounded-sm border flex items-center gap-1 ${
                                u.is_banned
                                  ? "border-[#2A9D8F]/40 text-[#2A9D8F] hover:bg-[#2A9D8F]/10"
                                  : "border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
                              }`}
                            >
                              {u.is_banned ? <><Check size={12} />Unban</> : <><Ban size={12} />Ban</>}
                            </button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  data-testid={`user-delete-${u.id}`}
                                  className="px-2 py-1 text-xs uppercase tracking-widest rounded-sm border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1"
                                >
                                  <Trash2 size={12} />Delete
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#0f0f0f] border-white/10 text-white">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="font-display uppercase">Delete {u.email}?</AlertDialogTitle>
                                  <AlertDialogDescription className="text-white/70">
                                    This permanently removes the user and all associated data — profile, videos, messages, invitations, ratings, notifications, and relocations. This cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-transparent border-white/20 hover:bg-white/10">Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteUser(u)}
                                    data-testid={`user-delete-confirm-${u.id}`}
                                    className="bg-red-500 hover:bg-red-600 text-white"
                                  >Delete permanently</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <div className="space-y-3">
              {inquiries.length === 0 && <div className="text-white/50">No inquiries yet.</div>}
              {inquiries.map((i) => (
                <div key={i.id} className="border border-white/10 bg-[#141414] p-5" data-testid={`inquiry-${i.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-64">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-[#eab308]" />
                        <div className="font-display uppercase font-bold">{i.subject}</div>
                      </div>
                      <div className="text-xs text-white/50 mt-1">
                        <a href={`mailto:${i.email}`} className="hover:text-[#e65f2b]">{i.name} · {i.email}</a> · {new Date(i.created_at).toLocaleString()}
                      </div>
                      <p className="text-sm mt-3 text-white/80 whitespace-pre-wrap">{i.message}</p>
                    </div>
                    <Select value={i.status} onValueChange={(s) => updateStatus(i.id, s)}>
                      <SelectTrigger data-testid={`inquiry-status-${i.id}`} className="w-40 bg-transparent border-white/20 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl flex items-center gap-2">
              <UserIcon size={20} className="text-[#e65f2b]" />
              {detail?.user?.name}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5">
              <Section title="Registration">
                <Row label="Full name" value={detail.user.name} />
                <Row label="Email" value={detail.user.email} />
                <Row label="Role" value={detail.user.role?.toUpperCase()} highlight />
                <Row label="Status" value={detail.user.is_banned ? "BANNED" : "ACTIVE"} highlight={!detail.user.is_banned} danger={detail.user.is_banned} />
                <Row label="Phone" value={detail.user.phone} />
                <Row label="Country" value={detail.user.country || detail.user.region} />
                <Row label="City" value={detail.user.city} />
                <Row label="Heard about us" value={detail.user.referral_source?.toUpperCase()} highlight />
                <Row label="Joined" value={(detail.user.created_at || "").slice(0, 10)} />
              </Section>

              {detail.user.guardian_name && (
                <Section title="Parent / Guardian (under 18)">
                  <Row label="Guardian name" value={detail.user.guardian_name} />
                  <Row label="Guardian phone" value={detail.user.guardian_phone} />
                  <Row label="Guardian email" value={detail.user.guardian_email} />
                </Section>
              )}

              {detail.profile && (
                <>
                  <Section title="Player Profile">
                    <Row label="Age" value={detail.profile.age} />
                    <Row label="Position" value={detail.profile.position} />
                    <Row label="Height / Weight" value={`${detail.profile.height_cm || '—'} cm · ${detail.profile.weight_kg || '—'} kg`} />
                    <Row label="Nationality" value={detail.profile.nationality} />
                    <Row label="Country" value={detail.profile.country} />
                    <Row label="City" value={detail.profile.city} />
                    <Row label="Academy" value={detail.profile.academy} />
                    <Row label="Foot" value={detail.profile.dominant_foot} />
                    <Row label="Passport" value={detail.profile.passport_status} />
                    <Row label="Verified" value={detail.profile.verified ? "✓ Yes" : "No"} highlight={detail.profile.verified} />
                  </Section>

                  {/* VERIFY TOGGLE */}
                  <div className="border border-[#eab308]/30 bg-[#eab308]/5 p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="text-[#eab308]" size={22} />
                      <div>
                        <div className="font-display uppercase font-bold text-sm tracking-widest text-[#eab308]">Verify Player</div>
                        <div className="text-xs text-white/60 mt-1">Activates the gold verification badge on the player&apos;s profile and lifts them to the top of scout search results.</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs uppercase tracking-widest ${detail.profile.verified ? "text-[#eab308]" : "text-white/40"}`}>
                        {detail.profile.verified ? "Verified" : "Off"}
                      </span>
                      <Switch
                        data-testid="admin-verify-toggle"
                        checked={!!detail.profile.verified}
                        disabled={verifyToggling}
                        onCheckedChange={toggleVerify}
                        className="data-[state=checked]:bg-[#eab308]"
                      />
                    </div>
                  </div>

                  {/* ADMIN NOTES */}
                  <div className="border border-white/10 bg-[#141414] p-4">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] mb-3">Verification Notes · Private</div>
                    <div className="text-xs text-white/50 mb-2">Details from video interviews or background checks. Only admins can see this.</div>
                    <Textarea
                      data-testid="admin-notes-textarea"
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      placeholder="e.g. Video interview Feb 3: confirmed age via passport, plays for XYZ academy since 2022, guardian verified…"
                      className="bg-black/40 border-white/20 min-h-32 text-white focus:border-[#e65f2b]"
                    />
                    <div className="mt-3 flex justify-end">
                      <Button
                        data-testid="admin-notes-save"
                        onClick={saveNotes}
                        disabled={savingNotes}
                        className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-9"
                      >
                        <Save size={14} className="mr-2" />{savingNotes ? "Saving…" : "Save notes"}
                      </Button>
                    </div>
                  </div>

                  {/* HIGHLIGHT REEL VIDEOS */}
                  <div className="border border-white/10 bg-[#141414] p-4" data-testid="admin-videos-section">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] mb-3 flex items-center gap-2">
                      <Play size={12} /> Highlight reel · {(detail.videos || []).length} video{(detail.videos || []).length === 1 ? "" : "s"}
                    </div>
                    {(detail.videos || []).length === 0 ? (
                      <div className="text-xs text-white/50">This player hasn&apos;t uploaded any videos yet.</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {detail.videos.map((v) => (
                          <div key={v.id} className="border border-white/10 bg-black" data-testid={`admin-video-${v.id}`}>
                            <video
                              controls
                              src={fileUrl(v.storage_path)}
                              poster={v.thumbnail_path ? fileUrl(v.thumbnail_path) : undefined}
                              className="w-full aspect-video bg-black"
                            />
                            <div className="p-3">
                              <div className="font-display uppercase text-sm font-bold truncate">{v.title || "Untitled"}</div>
                              <div className="text-[10px] text-white/50 uppercase tracking-widest mt-1">
                                {v.category || "—"} · {(v.created_at || "").slice(0, 10)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ADMIN → PLAYER MESSAGING */}
                  <div className="border border-[#e65f2b]/30 bg-[#e65f2b]/5 p-4" data-testid="admin-messaging-section">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] mb-3 flex items-center gap-2">
                      <Mail size={12} /> Message this player
                    </div>
                    <div className="text-xs text-white/60 mb-3">
                      Direct message with the player. They can reply from their dashboard.
                    </div>

                    {threadMsgs.length > 0 && (
                      <div className="max-h-48 overflow-y-auto border border-white/10 bg-black/40 p-3 mb-3 space-y-2" data-testid="admin-thread-scroll">
                        {threadMsgs.map((m) => {
                          const isFromAdmin = m.from_user_id !== detail.user.id;
                          return (
                            <div key={m.id} className={`flex ${isFromAdmin ? "justify-end" : "justify-start"}`}>
                              <div className={`max-w-[75%] px-3 py-2 text-sm ${isFromAdmin ? "bg-[#e65f2b] text-black" : "bg-white/10 text-white"}`}>
                                <div className="text-[10px] uppercase tracking-widest opacity-70">
                                  {isFromAdmin ? "You (Admin)" : (m.from_name || "Player")} · {(m.created_at || "").slice(11, 16)}
                                </div>
                                <div className="mt-1 whitespace-pre-wrap break-words">{m.text}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Textarea
                      data-testid="admin-message-textarea"
                      value={msgDraft}
                      onChange={(e) => setMsgDraft(e.target.value)}
                      placeholder="Write a message to this player…"
                      className="bg-black/40 border-white/20 min-h-24 text-white focus:border-[#e65f2b]"
                    />
                    <div className="mt-3 flex justify-end">
                      <Button
                        data-testid="admin-send-message-btn"
                        onClick={sendMessageToPlayer}
                        disabled={sendingMsg || !msgDraft.trim()}
                        className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-9"
                      >
                        <Send size={14} className="mr-2" />{sendingMsg ? "Sending…" : "Send"}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              <Section title="Activity">
                <Row label="Videos" value={detail.stats.videos} />
                <Row label="Scouting reports" value={detail.stats.ratings} />
                <Row label="Invitations" value={detail.stats.invitations} />
              </Section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-4">
      <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b] mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value, highlight, danger }) {
  const color = danger ? "text-red-400" : highlight ? "text-[#eab308]" : "text-white";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className={`mt-1 font-bold ${color}`}>{value || "—"}</div>
    </div>
  );
}
